import { getJSON } from 'common/services/api'
import { useQuery, useQueryClient } from 'react-query'
import { getQueryParams } from 'utils/getQueryParams'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'

import {
  REVISIONS_QUERY_KEY,
  REVISIONS_UPDATE_COUNTS_URL,
} from './queryConstants'
import { RotaBuilderFiltersType } from 'screens/Rostering/types'
import { DAILY } from '../constants'
import { YEAR_FIRST_DATE_FORMAT } from '../utils.datetime'
import { showToast } from 'common/utils'

dayjs.extend(isoWeek)

type UseCountChangesReturn = {
  countChangesData: CountChangesResponse
  isLoadingCountChanges: boolean
  refetchCountChanges: () => void
}

type UseCountChangesProps = {
  id?: string
  selectedDate?: Date
  intervalType?: 'daily' | 'weekly'
  includeWarnings?: boolean
  enabled?: boolean
  filterParams?: RotaBuilderFiltersType
}

type DetailType =
  | {
      instanceId: string
      memberId: string
      careTeam: 'Yes' | 'No'
      gender: 'Yes' | 'No'
      skills: `${number}/${number}`
      rotaTags: `${number}/${number}`
    }
  | {
      instanceId: string
      missingAllocations: `Missing ${number} allocations`
    }
  | {
      teamMemberId: string
      instanceIds: string[]
      overlap: boolean
    }

export type CountChangesResponse = {
  draftEvents: number
  draftWithChanges: number
  changesToPublishedEvents: number
  totalUpdates: number
  totalEvents: number
  filteredDrafts: number
  filteredDraftChanges: number
  filteredPublishedChanges: number
  filteredTotal: number
  filteredUpdates: number
  creators: {
    id: string
    name: string
  }[]
  warnings?: {
    missingChangedAllocations: number
    missingDraftAllocations: number
    missingPublishedAllocations: number
    missingChangedDraftAllocations: number
    warningChangedStatus: number
    warningDraftStatus: number
    warningPublishedStatus: number
    warningChangedDraftStatus: number
    details: DetailType[]
    instanceWarningMaps: any
  }
}

const useCountChanges = ({
  selectedDate,
  intervalType,
  includeWarnings,
  enabled,
  filterParams,
}: UseCountChangesProps): UseCountChangesReturn => {
  const queryClient = useQueryClient()

  intervalType = intervalType ?? 'weekly'

  const fromDate =
    intervalType === DAILY
      ? dayjs(selectedDate).format(YEAR_FIRST_DATE_FORMAT)
      : dayjs(selectedDate).startOf('isoWeek').format(YEAR_FIRST_DATE_FORMAT)

  const countChangesDates = {
    fromDate,
    intervalType,
    includeWarnings,
  }

  const {
    isLoading: isLoadingCountChanges,
    data: countChangesData,
    refetch: refetchCountChanges,
  } = useQuery(
    [
      REVISIONS_QUERY_KEY,
      'count',
      fromDate,
      intervalType,
      includeWarnings,
      filterParams,
    ],
    async () => {
      const data = await getJSON({
        url: `${REVISIONS_UPDATE_COUNTS_URL}?${getQueryParams({
          ...countChangesDates,
          ...filterParams,
          instanceTypes: filterParams?.typeOfEvent,
        })}`,
        isRota: true,
      })

      return data
    },
    {
      enabled,
      onSuccess(data) {
        if (!includeWarnings) {
          const key = [
            REVISIONS_QUERY_KEY,
            'count',
            fromDate,
            intervalType,
            true,
            filterParams,
          ]

          queryClient.setQueryData(key, data)
          queryClient.invalidateQueries(key)
        }
      },
      onError: (e: { message?: string }) => {
        showToast({
          type: 'error',
          title: e.message || 'Oops! Something went wrong.',
        })
      },
    }
  )

  return {
    countChangesData: {
      totalUpdates: 0,
      draftEvents: 0,
      changesToPublishedEvents: 0,
      warnings: {
        missingAllocations: 0,
        warningStatus: 0,
      },
      totalEvents: undefined,
      ...countChangesData,
    },
    refetchCountChanges,
    isLoadingCountChanges,
  }
}

export default useCountChanges
